import { createEffect, createMemo, createSignal, For, onCleanup, Show } from "solid-js";
import TextBox from "./guages/TextBox";
import {
  fetchManeuverTimeseriesCache,
  getManeuverChannelValueAtTime,
  type ManeuverTimeseriesCacheEntry,
} from "../../store/maneuverTimeseriesCache";
import type { ManeuverTextOverlayConfig } from "../../utils/maneuverVideoOverlayConfig";
export type { ManeuverTextOverlayConfig, ManeuverTextOverlaySeriesItem } from "../../utils/maneuverVideoOverlayConfig";
import type { ManeuversContext } from "../../utils/maneuversConfig";
import { phase, eventType } from "../../store/globalStore";
import { maneuverVideoStartOffsetSeconds, selectedTime } from "../../store/playbackStore";
import {
  applyTileOverlayPosition,
  computeManeuverOverlayFitScale,
  computeManeuverOverlayFitScaleFromMeasurement,
  estimateManeuverOverlayContentHeight,
  maneuverOverlayAvailableHeight,
  MANEUVER_OVERLAY_DEFAULT_ROW_HEIGHT,
  MANEUVER_OVERLAY_MIN_TEXTBOX_HEIGHT,
} from "../../utils/overlayPositionResolve";

interface ManeuverTextOverlayProps {
  eventId: number;
  maneuverDate: Date | null;
  /** First selected maneuver datetime — shared playback offset for all tiles (matches video sync). */
  referenceManeuverDate?: Date | null;
  eventIds: number[];
  context: ManeuversContext;
  config: ManeuverTextOverlayConfig | null;
}

function hexToRgba(hex: string, opacity: number): string {
  const normalized = (hex || "#FFFFFF").replace("#", "");
  if (normalized.length !== 6) return `rgba(255, 255, 255, ${opacity})`;
  const r = parseInt(normalized.slice(0, 2), 16);
  const g = parseInt(normalized.slice(2, 4), 16);
  const b = parseInt(normalized.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

const MAX_FIT_PASSES = 4;

export default function ManeuverTextOverlay(props: ManeuverTextOverlayProps) {
  const [cacheEntry, setCacheEntry] = createSignal<ManeuverTimeseriesCacheEntry | undefined>(undefined);
  const [loading, setLoading] = createSignal(false);
  const [fitScale, setFitScale] = createSignal(1);
  const [tileSize, setTileSize] = createSignal({ width: 0, height: 0 });
  let overlayElRef: HTMLDivElement | undefined;
  let tileWrapperRef: HTMLElement | null = null;
  let resizeObserver: ResizeObserver | undefined;
  let fitPassTimeout: ReturnType<typeof setTimeout> | undefined;

  const channelNames = createMemo(() =>
    (props.config?.series ?? [])
      .map((s) => s.name)
      .filter((name): name is string => typeof name === "string" && name.trim().length > 0)
  );

  const seriesCount = () => props.config?.series?.length ?? 0;

  /** Maneuver video overlays stack vertically on the tile edge regardless of builder orientation label. */
  const orientation = () => "vertical" as const;

  const contentHeightEstimate = createMemo(() =>
    estimateManeuverOverlayContentHeight(props.config?.series ?? [], {
      orientation: orientation(),
      forceVerticalStack: true,
    })
  );

  const cacheParams = createMemo(() => ({
    context: props.context,
    phase: phase(),
    eventType: eventType(),
    eventIds: props.eventIds,
    channels: channelNames(),
  }));

  const relativeTimeSec = createMemo(() => {
    const st = selectedTime();
    const refDate = props.referenceManeuverDate ?? props.maneuverDate;
    if (!refDate || !(st instanceof Date) || Number.isNaN(st.getTime())) {
      return maneuverVideoStartOffsetSeconds();
    }
    // Same logical offset for every tile: selectedTime relative to first maneuver (see currentTimeForTileForIndex in Video.tsx).
    return (st.getTime() - refDate.getTime()) / 1000;
  });

  createEffect(() => {
    const params = cacheParams();
    if (!props.config?.series?.length || params.eventIds.length === 0 || params.channels.length === 0) {
      setCacheEntry(undefined);
      return;
    }

    const controller = new AbortController();
    setLoading(true);
    void fetchManeuverTimeseriesCache({
      ...params,
      signal: controller.signal,
    })
      .then((entry) => {
        if (!controller.signal.aborted) {
          setCacheEntry(entry);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    // onCleanup, not a returned function: Solid discards createEffect return values, so the
    // in-flight fetch was never aborted and a stale response could clobber the newer cache entry.
    onCleanup(() => {
      controller.abort();
    });
  });

  createEffect(() => {
    const el = overlayElRef;
    const pos = props.config?.position ?? { x: 100, y: 50 };
    if (!el) return;
    applyTileOverlayPosition(el, pos, 8, 1);
  });

  const recomputeFitScale = (measuredHeight?: number) => {
    const { width, height } = tileSize();
    if (width <= 0 || height <= 0) return;

    const availableHeight = maneuverOverlayAvailableHeight(height);
    let next = computeManeuverOverlayFitScale({
      tileWidth: width,
      tileHeight: height,
      contentHeight: contentHeightEstimate(),
      orientation: orientation(),
    });

    if (measuredHeight != null && measuredHeight > 0) {
      next = computeManeuverOverlayFitScaleFromMeasurement(
        measuredHeight,
        availableHeight,
        next
      );
    }

    setFitScale((prev) => (Math.abs(next - prev) >= 0.005 ? next : prev));
  };

  createEffect(() => {
    tileSize();
    contentHeightEstimate();
    seriesCount();
    recomputeFitScale();
  });

  const scheduleMeasurementPasses = (pass = 0) => {
    if (fitPassTimeout) clearTimeout(fitPassTimeout);
    fitPassTimeout = setTimeout(() => {
      const el = overlayElRef;
      const { height } = tileSize();
      if (!el || height <= 0) return;

      const availableHeight = maneuverOverlayAvailableHeight(height);
      const measured = el.scrollHeight;
      if (measured > availableHeight + 1) {
        const refined = computeManeuverOverlayFitScaleFromMeasurement(
          measured,
          availableHeight,
          fitScale()
        );
        setFitScale((prev) => (refined < prev - 0.005 ? refined : prev));
        if (pass + 1 < MAX_FIT_PASSES) {
          scheduleMeasurementPasses(pass + 1);
        }
      }
    }, pass === 0 ? 0 : 50);
  };

  createEffect(() => {
    fitScale();
    loading();
    tileSize();
    seriesCount();
    scheduleMeasurementPasses(0);
  });

  /** Builder height as a multiplier on the default row height (60px = 1). */
  const rowScale = (designHeight?: number) => {
    const base = designHeight ?? MANEUVER_OVERLAY_DEFAULT_ROW_HEIGHT;
    if (!Number.isFinite(base) || base <= 0) { return 1; }
    return base / MANEUVER_OVERLAY_DEFAULT_ROW_HEIGHT;
  };

  const scaledTextBoxHeight = (designHeight?: number) => {
    const base = designHeight ?? MANEUVER_OVERLAY_DEFAULT_ROW_HEIGHT;
    return Math.max(
      MANEUVER_OVERLAY_MIN_TEXTBOX_HEIGHT,
      Math.round(base * fitScale())
    );
  };

  const attachResizeObserver = (overlayEl: HTMLDivElement) => {
    resizeObserver?.disconnect();
    const wrapper = overlayEl.parentElement;
    if (!wrapper || typeof ResizeObserver === "undefined") return;

    tileWrapperRef = wrapper;

    const updateSize = (width: number, height: number) => {
      if (width <= 0 || height <= 0) return;
      setTileSize((prev) =>
        Math.abs(prev.width - width) >= 1 || Math.abs(prev.height - height) >= 1
          ? { width, height }
          : prev
      );
    };

    updateSize(wrapper.clientWidth, wrapper.clientHeight);

    resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      const width = entry?.contentRect?.width ?? wrapper.clientWidth;
      const height = entry?.contentRect?.height ?? wrapper.clientHeight;
      updateSize(width, height);
    });
    resizeObserver.observe(wrapper);
  };

  onCleanup(() => {
    if (fitPassTimeout) clearTimeout(fitPassTimeout);
    resizeObserver?.disconnect();
    resizeObserver = undefined;
    tileWrapperRef = null;
  });

  const dataRowForChannel = (channelName: string): Record<string, number> => {
    const value = getManeuverChannelValueAtTime(
      cacheEntry(),
      props.eventId,
      channelName,
      relativeTimeSec()
    );
    if (value === null) return {};
    return { [channelName]: value, [channelName.toLowerCase()]: value };
  };

  return (
    <Show when={props.config?.series?.length}>
      <div
        ref={(el) => {
          overlayElRef = el;
          if (el) attachResizeObserver(el);
        }}
        class="overlay-container--tile maneuver-text-overlay maneuver-text-overlay-container maneuver-text-overlay-vertical"
        style={{
          "background-color": hexToRgba(props.config?.backgroundColor ?? "#FFFFFF", props.config?.opacity ?? 1),
          "--maneuver-overlay-fit-scale": String(fitScale()),
        }}
      >
        <For each={props.config?.series ?? []}>
          {(item) => (
            /* Per-row wrapper carries the builder's height as a scale var. The overlay's font-size
               rules use !important (to beat TextBox's inline styles), so they can't pick up the
               height from the TextBox height prop — they read this var instead. */
            <div
              class="maneuver-text-overlay-row"
              style={{ "--maneuver-overlay-row-scale": String(rowScale(item.height)) }}
            >
              <TextBox
                label={item.primaryChannelLabel || item.name}
                labelColor={item.color}
                channelName={item.name}
                hasTarget={false}
                height={scaledTextBoxHeight(item.height)}
                dataRow={() => dataRowForChannel(item.name)}
              />
            </div>
          )}
        </For>
      </div>
    </Show>
  );
}
